import { ref } from 'vue'

/**
 * Composable for handling Spotify image download and processing
 * Provides functionality for downloading, caching, and processing album artwork
 */
export function useSpotifyImageService() {
  const imageLoading = ref(false)
  const imageError = ref(null)
  const downloadedImage = ref(null)
  const imagePreviewUrl = ref(null)
  
  /**
   * Extract and download album image from Spotify metadata
   * @param {Object} spotifyData - Spotify album metadata
   * @returns {Promise<Object>} - Image data with blob and URL
   */
  const downloadAlbumImage = async (spotifyData) => {
    imageLoading.value = true
    imageError.value = null
    downloadedImage.value = null
    imagePreviewUrl.value = null
    
    try {
      // Validate Spotify data
      if (!spotifyData || !spotifyData.images || !Array.isArray(spotifyData.images)) {
        throw new Error('No album artwork available from Spotify')
      }
      
      // Find the best quality image (640x640 preferred, or largest available)
      let selectedImage = null
      
      // Try to find 640x640 image first (standard Spotify album size)
      selectedImage = spotifyData.images.find(img => img.width === 640 && img.height === 640)
      
      // If not found, get the largest image available
      if (!selectedImage && spotifyData.images.length > 0) {
        selectedImage = spotifyData.images.reduce((prev, current) => {
          return (current.width * current.height) > (prev.width * prev.height) ? current : prev
        })
      }
      
      if (!selectedImage || !selectedImage.url) {
        throw new Error('No valid album artwork URL found in Spotify data')
      }
      
      console.log('Downloading Spotify album image:', {
        url: selectedImage.url,
        dimensions: `${selectedImage.width}x${selectedImage.height}`,
        albumName: spotifyData.name,
        artist: spotifyData.artists?.[0]?.name
      })
      
      // Download the image with CORS handling
      const response = await fetch(selectedImage.url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'image/*'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.status} ${response.statusText}`)
      }
      
      // Validate content type
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.startsWith('image/')) {
        throw new Error(`Invalid content type: ${contentType}. Expected image format.`)
      }
      
      // Convert to blob
      const blob = await response.blob()
      
      // Validate image size (max 10MB for safety)
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (blob.size > maxSize) {
        throw new Error(`Image too large: ${Math.round(blob.size / 1024 / 1024)}MB. Maximum allowed: 10MB`)
      }
      
      // Create object URL for preview
      const previewUrl = URL.createObjectURL(blob)
      
      const imageData = {
        blob,
        url: selectedImage.url,
        previewUrl,
        width: selectedImage.width,
        height: selectedImage.height,
        size: blob.size,
        type: contentType,
        metadata: {
          albumName: spotifyData.name,
          artist: spotifyData.artists?.[0]?.name || 'Unknown Artist',
          source: 'spotify',
          downloadedAt: new Date().toISOString()
        }
      }
      
      // Cache the downloaded image
      downloadedImage.value = imageData
      imagePreviewUrl.value = previewUrl
      
      console.log('Successfully downloaded Spotify album image:', {
        size: `${Math.round(blob.size / 1024)}KB`,
        type: contentType,
        dimensions: `${selectedImage.width}x${selectedImage.height}`
      })
      
      // Debug: Confirm reactive values are set
      console.log('Composable debug - Values set:', {
        downloadedImageSet: !!downloadedImage.value,
        imagePreviewUrlSet: !!imagePreviewUrl.value,
        previewUrl
      })
      
      return imageData
      
    } catch (error) {
      console.error('Failed to download Spotify album image:', error)
      
      // Provide user-friendly error messages
      let userMessage = 'Failed to download album artwork from Spotify'
      
      if (error.message.includes('No album artwork available')) {
        userMessage = 'This album does not have artwork available on Spotify'
      } else if (error.message.includes('Failed to download image')) {
        userMessage = 'Unable to download album artwork. Please try again or upload manually.'
      } else if (error.message.includes('Invalid content type')) {
        userMessage = 'Album artwork format is not supported'
      } else if (error.message.includes('Image too large')) {
        userMessage = error.message
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        userMessage = 'Network error while downloading artwork. Please check your connection.'
      }
      
      imageError.value = userMessage
      throw new Error(userMessage)
    } finally {
      imageLoading.value = false
    }
  }
  
  /**
   * Clear cached image data and clean up object URLs
   */
  const clearImageCache = () => {
    if (imagePreviewUrl.value) {
      URL.revokeObjectURL(imagePreviewUrl.value)
    }
    
    downloadedImage.value = null
    imagePreviewUrl.value = null
    imageError.value = null
  }
  
  /**
   * Validate image dimensions and file size
   * @param {Object} imageData - Image data from downloadAlbumImage
   * @returns {Object} - Validation result
   */
  const validateImageData = (imageData) => {
    const validation = {
      isValid: true,
      errors: [],
      warnings: []
    }
    
    // Check minimum dimensions
    const minDimension = 100
    if (imageData.width < minDimension || imageData.height < minDimension) {
      validation.errors.push(`Image too small: ${imageData.width}x${imageData.height}. Minimum: ${minDimension}x${minDimension}`)
      validation.isValid = false
    }
    
    // Check maximum dimensions
    const maxDimension = 2000
    if (imageData.width > maxDimension || imageData.height > maxDimension) {
      validation.warnings.push(`Large image: ${imageData.width}x${imageData.height}. Will be resized for optimal storage.`)
    }
    
    // Check file size
    const maxSize = 5 * 1024 * 1024 // 5MB
    if (imageData.size > maxSize) {
      validation.warnings.push(`Large file: ${Math.round(imageData.size / 1024 / 1024)}MB. Will be compressed for optimal storage.`)
    }
    
    // Check aspect ratio (should be roughly square for album covers)
    const aspectRatio = imageData.width / imageData.height
    if (aspectRatio < 0.8 || aspectRatio > 1.2) {
      validation.warnings.push('Image is not square. Album covers work best with square aspect ratios.')
    }
    
    return validation
  }
  
  return {
    // State
    imageLoading,
    imageError,
    downloadedImage,
    imagePreviewUrl,
    
    // Methods
    downloadAlbumImage,
    clearImageCache,
    validateImageData
  }
}